import type { ClinicalSpecialty } from '../types/models/users';

/**
 * Whether cases are active for a given specialty in this org.
 * - uses_cases=false → false
 * - uses_cases=true + no case_specialties → true (all specialties)
 * - uses_cases=true + case_specialties set → only listed specialties
 */
export function usesCasesForSpecialty(
  orgSettings: { uses_cases?: boolean; case_specialties?: ClinicalSpecialty[] } | undefined,
  specialty: ClinicalSpecialty | string | undefined
): boolean {
  if (!orgSettings?.uses_cases) return false;
  if (!orgSettings.case_specialties?.length) return true;
  if (!specialty) return false;
  return orgSettings.case_specialties.includes(specialty as ClinicalSpecialty);
}

/**
 * Whether form sends are scoped to cases for a given specialty.
 * Cases must be active for the specialty (usesCasesForSpecialty) AND
 * settings.case_scoped_forms must be true. Default off: forms are patient-wide
 * across all cases unless the org opts in.
 */
export function caseScopedForms(
  orgSettings: { uses_cases?: boolean; case_specialties?: ClinicalSpecialty[]; case_scoped_forms?: boolean } | undefined,
  specialty: ClinicalSpecialty | string | undefined
): boolean {
  return usesCasesForSpecialty(orgSettings, specialty) && !!orgSettings?.case_scoped_forms;
}

/**
 * Whether a referring provider is required on cases for a given specialty.
 * - require_case_referring_provider=false → false
 * - true + no referring_provider_specialties → true (all specialties)
 * - true + referring_provider_specialties set → only listed specialties
 */
export function requiresReferringProvider(
  orgSettings: { require_case_referring_provider?: boolean; referring_provider_specialties?: ClinicalSpecialty[] | null } | undefined,
  specialty: ClinicalSpecialty | string | undefined
): boolean {
  if (!orgSettings?.require_case_referring_provider) return false;
  if (!orgSettings.referring_provider_specialties?.length) return true;
  if (!specialty) return false;
  return orgSettings.referring_provider_specialties.includes(specialty as ClinicalSpecialty);
}
